import { useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { PlantillaCard } from "@/components/PlantillaCard";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, PackageCheck, AlertTriangle, Eraser, Loader2, Merge } from "lucide-react";

export const PlantillaMaestraBloque = ({ maestra, generadas, isAdmin, cardProps, onGenerarFaltantes, onReparar, onAgrupar }) => {
  const [cob, setCob] = useState(null);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [limpiando, setLimpiando] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get(`/plantillas/${maestra.id}/cobertura`)
      .then(({ data }) => { if (!cancel) setCob(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [maestra.id]);

  const limpiarHuerfanos = async () => {
    setLimpiando(true);
    try {
      const { data } = await api.post(`/plantillas/${maestra.id}/limpiar-padres-huerfanos`);
      toast.success(`Referencia(s) huérfana(s) quitada(s): ${data.eliminados}`);
      onReparar?.();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setLimpiando(false); }
  };

  const padresHuerfanos = maestra.padres_inexistentes?.length || 0;

  return (
    <div data-testid={`maestra-bloque-${maestra.id}`} className="border border-amber-200 bg-amber-50/40 rounded-sm p-2 space-y-2">
      {padresHuerfanos > 0 && isAdmin && (
        <div data-testid={`herencia-rota-aviso-${maestra.id}`}
          className="flex flex-wrap items-center gap-2 bg-amber-100 border border-amber-300 text-amber-800 rounded-sm px-2.5 py-1.5 text-xs">
          <AlertTriangle size={14} className="shrink-0" />
          <span className="flex-1 min-w-[180px]">Esta plantilla hereda de {padresHuerfanos} plantilla(s) que ya no existe(n).</span>
          <Button data-testid={`limpiar-huerfanos-${maestra.id}`} size="sm" variant="outline"
            disabled={limpiando} className="h-7 border-amber-400 text-amber-800 hover:bg-amber-200"
            onClick={limpiarHuerfanos}>
            {limpiando ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Eraser size={12} className="mr-1" />}
            Quitar referencia huérfana
          </Button>
        </div>
      )}

      <PlantillaCard {...cardProps(maestra)}
        mostrarGenerarFaltantes={isAdmin && !!cob && cob.faltantes.length > 0}
        puedeGenerarIA={isAdmin && !!cob && (cob.faltantes.length > 0 || cob.tiene_fijos)}
        onGenerarFaltantes={() => onGenerarFaltantes(maestra, cob.faltantes_ids)} />

      <div data-testid={`cobertura-${maestra.id}`} className="text-xs text-muted-foreground px-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <PackageCheck size={13} className="text-teal-600 shrink-0" />
        {!cob ? <span>Calculando cobertura…</span> : (<>
          <span>Cubre <b className="text-foreground">{cob.cubiertos_count}</b> de {cob.aplicables_count} modelo(s) efectivo(s)</span>
          {cob.faltantes.length > 0 ? (
            <span className="text-pink-700" data-testid={`cobertura-faltantes-${maestra.id}`}>
              · sin plantilla hija: {cob.faltantes.map((m) => m.nombre_mostrar).join(", ")}
            </span>
          ) : cob.aplicables_count > 0 ? (
            <span className="text-teal-700">· todas las hijas generadas</span>
          ) : cob.tiene_fijos ? (
            <span data-testid={`cobertura-fijos-${maestra.id}`}>· repuestos fijos: elegí a qué modelos copiarlos</span>
          ) : (
            <span>· sin modelos efectivos</span>
          )}
        </>)}
      </div>

      {generadas.length > 0 && (
        <div className="px-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button data-testid={`toggle-generadas-${maestra.id}`} onClick={() => setChildrenOpen((o) => !o)}
              className="flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900">
              {childrenOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Generadas ({generadas.length})
            </button>
            {isAdmin && generadas.length >= 2 && (
              <Button data-testid={`agrupar-equivalentes-btn-${maestra.id}`} size="sm" variant="outline"
                className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                onClick={() => onAgrupar?.(maestra)}>
                <Merge size={12} className="mr-1" /> Agrupar plantillas equivalentes
              </Button>
            )}
          </div>
          {childrenOpen && (
            <div className="mt-2 pl-3 border-l-2 border-indigo-200 grid grid-cols-1 md:grid-cols-2 gap-2">
              {generadas.map((g) => <PlantillaCard key={g.id} {...cardProps(g)} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
